import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { OpenAPI } from "@/client/core/OpenAPI"

// Types mirror backend TopicSuggestionPublic / TopicSuggestionsPublic.
// Hand-written (following useSuggestTopics.ts) until the generated client is
// refreshed via ./scripts/generate-client.sh.
export interface TopicSuggestionPublic {
  id: string
  question_id: string
  topic_id: string | null
  proposed_topic_name: string | null
  topic_name: string | null
  score: number | null
  status: string
  question_text: string
}

export interface TopicSuggestionsResponse {
  data: TopicSuggestionPublic[]
  count: number
}

function apiBaseUrl(): string {
  const base = (OpenAPI.BASE || import.meta.env.VITE_API_URL || "").replace(
    /\/$/,
    "",
  )
  if (!base) {
    throw new Error(
      "API base URL is not configured. Set VITE_API_URL to your backend origin.",
    )
  }
  return base
}

function authHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  }
}

async function getSuggestions(
  status: string,
): Promise<TopicSuggestionsResponse> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-suggestions?status=${status}`,
    { headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || "Failed to load topic suggestions")
  }
  return response.json()
}

async function postAction(suggestionId: string, action: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-suggestions/${suggestionId}/${action}`,
    { method: "POST", headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || `Failed to ${action} suggestion`)
  }
}

export function useTopicSuggestions(status = "pending") {
  return useQuery({
    queryKey: ["topic-suggestions", status],
    queryFn: () => getSuggestions(status),
  })
}

export function useConfirmTopicSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "confirm"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topic-suggestions"] }),
  })
}

export function useRejectTopicSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "reject"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topic-suggestions"] }),
  })
}
